import { useMutation, useQueryClient } from '@tanstack/react-query'
import { confirmSignIn } from 'aws-amplify/auth'

import type { Consumer } from 'src/features/consumers/schemas/consumer'
import { getRequest, postRequest } from 'src/lib/api-client'
import { logger } from 'src/lib/logger'
import { queries } from 'src/lib/query-keys'
import { storeTokens } from 'src/lib/token-provider'
import { sendEvent } from 'src/shared/api/events/send-event'

import { AUTH_ERROR_CODES, AuthError } from '../lib/auth-errors'

import { useSignOut } from './use-signout'

interface ConsumerCreateRecordRequestBody {
  firstName: string
  lastName: string
  primaryEmail?: string
  phoneNumber?: string
  refreshToken: string
}

interface ConsumerCreateRecordResponse {
  success: boolean
}

export async function createConsumerRecord(request: ConsumerCreateRecordRequestBody) {
  const result = await postRequest<ConsumerCreateRecordResponse>('/consumers/account', request)
  return result
}

interface ConsumerPhoneVerifyRequestBody {
  phoneNumber: string
  otpCode: string
  email?: string
  firstName?: string
  lastName?: string
  deviceId?: string
}

interface ConsumerPhoneVerifyResponse {
  success: boolean
  tokens: {
    accessToken: string
    idToken: string
    refreshToken: string
    expiresIn: number
  }
}

async function consumerPhoneVerify(request: ConsumerPhoneVerifyRequestBody) {
  try {
    const result = await postRequest<ConsumerPhoneVerifyResponse>('/auth/consumer/phone/verify', request)
    return result
  } catch (error) {
    if (error instanceof Error) {
      // Check for invalid/expired verification code error from backend
      if (error.message.includes('Invalid verification code') || error.message.includes('Invalid or expired code')) {
        throw new AuthError(AUTH_ERROR_CODES.INVALID_OTP, error.message)
      }
      // Check for rate limit error
      if (error.message.includes('Too many failed attempts')) {
        throw new AuthError(AUTH_ERROR_CODES.RATE_LIMITED, error.message)
      }
      // Check for professional account exists error
      if (error.message.includes('already registered as a contractor')) {
        throw new AuthError(AUTH_ERROR_CODES.PROFESSIONAL_EXISTS, error.message)
      }
    }
    throw error
  }
}

export const useConsumerPhoneSignInComplete = () => {
  const queryClient = useQueryClient()
  const signOut = useSignOut({ isConsumer: true })

  return useMutation({
    mutationFn: async ({
      phoneNumber,
      otpCode,
      email = '',
      firstName = '',
      lastName = '',
      deviceId,
    }: ConsumerPhoneVerifyRequestBody) => {
      const result = await consumerPhoneVerify({
        phoneNumber,
        otpCode,
        email,
        firstName,
        lastName,
        deviceId,
      })

      // Handle invalid response
      if (!result || result.success === false) {
        throw new AuthError(AUTH_ERROR_CODES.INVALID_RESPONSE, 'No response or success is false')
      }

      // Store tokens from backend for Amplify session management
      storeTokens({
        idToken: result.tokens.idToken,
        accessToken: result.tokens.accessToken,
        refreshToken: result.tokens.refreshToken,
      })

      // Check if consumer record exists.
      let consumerExist = true
      try {
        consumerExist = await consumerRecordExists()
      } catch (error) {
        await signOut.mutateAsync()
        throw new AuthError(AUTH_ERROR_CODES.INVALID_RESPONSE, 'Consumer record not found')
      }

      try {
        // If consumer record doesn't exist, create it
        if (!consumerExist) {
          await createConsumerRecord({
            firstName: '',
            lastName: '',
            phoneNumber: phoneNumber,
            refreshToken: result.tokens.refreshToken,
          })
        }
      } catch (error) {
        // Sign out the user
        await signOut.mutateAsync()
        if (error instanceof Error) {
          // Check for professional account exists error
          if (error.message.includes('already registered as a contractor')) {
            throw new AuthError(AUTH_ERROR_CODES.PROFESSIONAL_EXISTS, error.message)
          }
          if (error.message.includes('This phone number is already registered as a professional')) {
            throw new AuthError(AUTH_ERROR_CODES.PROFESSIONAL_EXISTS, error.message)
          }
        }
        throw error
      }

      // First invalidate auth queries to refresh the auth state
      await queryClient.invalidateQueries({ queryKey: queries.auth._def })

      // Then invalidate user data queries to fetch fresh data
      await queryClient.invalidateQueries({ queryKey: queries.consumers.me.queryKey })

      // Track successful sign in
      sendEvent({
        eventType: 'auth_consumer_phone_signin_success',
        info: { phoneNumber },
      })

      return result
    },
    onError: (error: unknown) => {
      const errorCode = error instanceof AuthError ? error.code : 'unknown'
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logger.error('Sign-in complete error:', errorCode, errorMessage)

      sendEvent({
        eventType: 'auth_consumer_phone_signin_complete_error',
        info: { error: errorCode, message: errorMessage },
      })
    },
  })
}

interface ConsumerEmailVerifyRequestBody {
  email: string
  otpCode: string
  session: string
}

interface ConsumerEmailVerifyResponse {
  success: boolean
  tokens: {
    accessToken: string
    idToken: string
    refreshToken: string
    expiresIn: number
  }
}

async function consumerRecordExists() {
  try {
    const result = await getRequest<Consumer>('/consumers/me')
    if (!result) {
      return false
    }
    return true
  } catch (error) {
    if (error instanceof Error && error.message.includes('No Current Consumer')) {
      return false
    }
    throw error
  }
}

async function consumerEmailVerify(request: ConsumerEmailVerifyRequestBody) {
  try {
    const result = await postRequest<ConsumerEmailVerifyResponse>('/auth/consumer/email/verify', request)
    return result
  } catch (error) {
    if (error instanceof Error) {
      // Check for invalid/expired verification code error from backend
      if (error.message.includes('Invalid verification code') || error.message.includes('Invalid or expired code')) {
        throw new AuthError(AUTH_ERROR_CODES.INVALID_OTP, error.message)
      }
      // Check for rate limit error
      if (error.message.includes('Too many failed attempts')) {
        throw new AuthError(AUTH_ERROR_CODES.RATE_LIMITED, error.message)
      }
      // Check for professional account exists error
      if (error.message.includes('already registered as a contractor')) {
        throw new AuthError(AUTH_ERROR_CODES.PROFESSIONAL_EXISTS, error.message)
      }
    }
    throw error
  }
}

// DEPRECATED - use useConsumerEmailSignInComplete instead. This uses the old Amplify sign in flow.
export const useConsumerSignInComplete = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ challengeResponse, signInResult }: { challengeResponse: string; signInResult: any }) => {
      const result = await confirmSignIn({
        challengeResponse,
      })

      if (result.isSignedIn) {
        // First invalidate auth queries to refresh the auth state
        await queryClient.invalidateQueries({ queryKey: queries.auth._def })

        // Then invalidate user data queries to fetch fresh data
        await queryClient.invalidateQueries({ queryKey: queries.consumers.me.queryKey })

        // Track successful sign in
        const email = signInResult.username
        sendEvent({
          eventType: 'auth_consumer_signin_success',
          info: { email },
        })
      }

      return result
    },
    onError: (error: unknown) => {
      const errorName = error instanceof Error ? error.name : 'unknown'
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Sign-in complete error:', errorName, errorMessage)

      sendEvent({
        eventType: 'auth_consumer_signin_complete_error',
        info: { error: errorName, message: errorMessage },
      })
    },
  })
}

export const useConsumerEmailSignInComplete = () => {
  const queryClient = useQueryClient()
  const signOut = useSignOut({ isConsumer: true })

  return useMutation({
    mutationFn: async ({ email, otpCode, session }: ConsumerEmailVerifyRequestBody) => {
      // Call backend with otp code and session to verify email
      const result = await consumerEmailVerify({ email, otpCode, session })

      // Handle invalid response
      if (!result || result.success === false) {
        throw new AuthError(AUTH_ERROR_CODES.INVALID_RESPONSE, 'No response or success is false')
      }

      // Store tokens from backend for Amplify session management
      storeTokens({
        idToken: result.tokens.idToken,
        accessToken: result.tokens.accessToken,
        refreshToken: result.tokens.refreshToken,
      })

      // Check if consumer record exists.
      let consumerExist = true
      try {
        consumerExist = await consumerRecordExists()
      } catch (error) {
        await signOut.mutateAsync()
        throw new AuthError(AUTH_ERROR_CODES.INVALID_RESPONSE, 'Consumer record not found')
      }

      try {
        // If consumer record doesn't exist, create it
        if (!consumerExist) {
          await createConsumerRecord({
            firstName: '',
            lastName: '',
            primaryEmail: email,
            refreshToken: result.tokens.refreshToken,
          })
        }
      } catch (error) {
        await signOut.mutateAsync()
        if (error instanceof Error && error.message.includes('already registered as a contractor')) {
          throw new AuthError(AUTH_ERROR_CODES.PROFESSIONAL_EXISTS, error.message)
        }
        throw error
      }

      // First invalidate auth queries to refresh the auth state
      await queryClient.invalidateQueries({ queryKey: queries.auth._def })

      // Then invalidate consumer data queries to fetch fresh data
      await queryClient.invalidateQueries({ queryKey: queries.consumers.me.queryKey })

      // Track successful sign in
      sendEvent({
        eventType: 'auth_consumer_signin_success',
        info: { email },
      })

      return result
    },
    onError: (error: unknown) => {
      const errorCode = error instanceof AuthError ? error.code : 'unknown'
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logger.error('Sign-in complete error:', errorCode, errorMessage)

      sendEvent({
        eventType: 'auth_consumer_signin_complete_error',
        info: { error: errorCode, message: errorMessage },
      })
    },
  })
}
